// Official passport photo requirements for each supported photo size.
//

import {
  Ban,
  Camera,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Ruler,
  User,
} from "lucide-react";
import { useState } from "react";

import { type PhotoSize } from "../utils/dimensions";

const REQUIREMENT_PROFILES = {
  "2x2": {
    title: "Official U.S. Passport Photo Requirements",
    officialUrl:
      "https://travel.state.gov/content/travel/en/passports/how-apply/photos.html",
    source: "U.S. Department of State",
    compactTitle: "Head size: 1 to 1⅜ inches (25–35 mm)",
    compactDescription:
      "Position your head so it fills approximately 50–69% of the frame height.",
    sections: {
      dimensions: {
        title: "Size & Dimensions",
        icon: Ruler,
        items: [
          "Photo size: 2 × 2 inches (51 × 51 mm)",
          "Head height: 1 to 1⅜ inches (25–35 mm) from chin to top of head",
          "Printed on matte or glossy photo quality paper",
        ],
      },
      quality: {
        title: "Image Quality",
        icon: Camera,
        items: [
          "High-resolution, not blurry, grainy, or pixelated",
          "Taken within the last 6 months",
          "In color with white or off-white background",
          "No shadows on face or background",
        ],
      },
      appearance: {
        title: "Appearance",
        icon: User,
        items: [
          "Neutral expression with mouth closed",
          "Both eyes open and clearly visible",
          "Face camera directly, no head tilting",
          "Remove glasses (medical exceptions require doctor's note)",
          "Remove hats/head coverings (religious/medical exceptions allowed)",
        ],
      },
      prohibited: {
        title: "Not Allowed",
        icon: Ban,
        items: [
          "No AI editing, filters, or digital alterations",
          "No uniforms or camouflage clothing",
          "No headphones or hands-free devices",
          "No photocopies or digitally-scanned images",
        ],
      },
    },
  },
  "33x48": {
    title: "Official Chinese Passport / Travel Document Photo Requirements",
    officialUrl:
      "https://newyork.china-consulate.gov.cn/zjfw/lshzhj/hzlxz/hzlxzzpyq/202312/t20231229_11214646.htm",
    source: "Consulate General of China in New York",
    compactTitle: "Head size: 28–33 mm",
    compactDescription:
      "Leave 3–5 mm between the top of the head and the top edge of the photo.",
    sections: {
      dimensions: {
        title: "Size & Dimensions",
        icon: Ruler,
        items: [
          "Photo size: 33 × 48 mm",
          "Head height: 28–33 mm from chin to top of head",
          "Head width: 15–22 mm",
          "Top of head: 3–5 mm below the upper edge",
        ],
      },
      quality: {
        title: "Image Quality",
        icon: Camera,
        items: [
          "Recent color photo taken within the last 6 months",
          "Plain white background",
          "No shadows on the face or background",
          "Clear, evenly lit, and not digitally altered",
        ],
      },
      appearance: {
        title: "Appearance",
        icon: User,
        items: [
          "Neutral expression with eyes open and lips closed",
          "Full face and head clearly visible",
          "Face camera directly with the head centered",
          "Show the neck and upper shoulders",
        ],
      },
      prohibited: {
        title: "Not Allowed",
        icon: Ban,
        items: [
          "No tinted or thick-rimmed glasses",
          "No glare, shadows, or frames obscuring the eyes",
          "No head coverings unless required for religious reasons",
          "No retouching or composite photos",
        ],
      },
    },
  },
} satisfies Record<
  PhotoSize,
  {
    title: string;
    officialUrl: string;
    source: string;
    compactTitle: string;
    compactDescription: string;
    sections: Record<
      string,
      {
        title: string;
        icon: typeof Ruler;
        items: string[];
      }
    >;
  }
>;

interface RequirementsProps {
  photoSize: PhotoSize;
  defaultExpanded?: boolean;
  compact?: boolean;
}

// Passport photo requirements component.
//
export function Requirements({
  photoSize,
  defaultExpanded = false,
  compact = false,
}: RequirementsProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const profile = REQUIREMENT_PROFILES[photoSize];

  if (compact) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
        <div className="flex items-start gap-3">
          <Ruler className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-sm">
            <p className="font-medium text-amber-800 dark:text-amber-200">
              {profile.compactTitle}
            </p>
            <p className="mt-1 text-amber-700 dark:text-amber-300">
              {profile.compactDescription}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-accent rounded-xl border">
      {/* Header */}
      <button
        className="flex w-full items-center justify-between p-4 text-left"
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">{profile.title}</span>
          <a
            className="text-primary hover:text-primary/80"
            href={profile.officialUrl}
            rel="noopener noreferrer"
            target="_blank"
            onClick={(event) => event.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
        {isExpanded ? (
          <ChevronUp className="text-muted-foreground h-5 w-5" />
        ) : (
          <ChevronDown className="text-muted-foreground h-5 w-5" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="border-t p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {Object.entries(profile.sections).map(([key, section]) => {
              const Icon = section.icon;

              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon className="text-muted-foreground h-4 w-4" />
                    <h4 className="font-medium">{section.title}</h4>
                  </div>
                  <ul className="text-muted-foreground space-y-1 text-sm">
                    {section.items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="bg-muted-foreground mt-1.5 h-1 w-1 flex-shrink-0 rounded-full" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <p className="text-muted-foreground mt-4 text-xs">
            Source:{" "}
            <a
              className="text-primary hover:underline"
              href={profile.officialUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              {profile.source}
            </a>
          </p>
        </div>
      )}
    </div>
  );
}
